import type { Translate } from 'next-translate';
import Image from 'next/image';
import React from 'react';
import type { LandingMainBenefit } from '@/types';
import fileGif from '@/public/images/landing/home/benefits/file-gif.webp';
import fileJpg from '@/public/images/landing/home/benefits/file-jpg.webp';
import fileMp3 from '@/public/images/landing/home/benefits/file-mp3.webp';
import filePdf from '@/public/images/landing/home/benefits/file-pdf.webp';
import filePng from '@/public/images/landing/home/benefits/file-png.webp';
import toolImageToText from '@/public/images/landing/home/benefits/tool-image-to-text.webp';
import toolSpeechToText from '@/public/images/landing/home/benefits/tool-speech-to-text.webp';
import styles from './BenefitVisual.module.scss';

interface Props {
	id: LandingMainBenefit['id'];
	t: Translate;
}

// Scattered format icons, in the design's stacking order (last on top)
const FILES = [
	{ id: 'jpg', src: fileJpg, size: 65 },
	{ id: 'png', src: filePng, size: 147 },
	{ id: 'gif', src: fileGif, size: 44 },
	{ id: 'pdf', src: filePdf, size: 51 },
	{ id: 'mp3', src: fileMp3, size: 45 },
] as const;

const SUGGESTIONS = [
	{ id: 'removeObjects', emoji: '✨' },
	{ id: 'hairstyle', emoji: '🎨' },
	{ id: 'portrait', emoji: '🪄' },
] as const;

const Tool: React.FC<{
	image: typeof toolSpeechToText;
	title: string;
	text: string;
	className: string;
}> = ({ image, title, text, className }) => (
	<div className={`${styles['tool']} ${className}`}>
		<Image src={image} alt='' width={86} height={86} />
		<span className={styles['tool__title']}>{title}</span>
		<span className={styles['tool__text']}>{text}</span>
	</div>
);

/**
 * The illustration at the bottom of each benefit card: a slice of the product's
 * UI, drawn in markup so it stays sharp and translated. Decorative only.
 */
export const BenefitVisual: React.FC<Props> = ({ id, t }) => {
	switch (id) {
		case 'skills':
			return (
				<div className={styles['prompt']} aria-hidden='true'>
					<span className={styles['prompt__button']}>
						{t('benefits_card1_improvePrompt')} ✨
					</span>
				</div>
			);

		case 'flow':
			return (
				<div className={styles['suggest']} aria-hidden='true'>
					<div className={styles['suggest__header']}>
						<span>{t('benefits_card2_continueTitle')}</span>
						<span className={styles['suggest__all']}>
							{t('benefits_card2_seeAll')}
						</span>
					</div>
					<div className={styles['suggest__list']}>
						{SUGGESTIONS.map(item => (
							<div key={item.id} className={styles['suggest__card']}>
								<span className={styles['suggest__emoji']}>{item.emoji}</span>
								<span className={styles['suggest__label']}>
									{t(`benefits_card2_${item.id}`)}
								</span>
								<span className={styles['suggest__arrow']}>→</span>
							</div>
						))}
					</div>
				</div>
			);

		case 'files':
			return (
				<div className={styles['files']} aria-hidden='true'>
					{FILES.map(file => (
						<Image
							key={file.id}
							src={file.src}
							alt=''
							width={file.size}
							height={file.size}
							className={`${styles['files__item']} ${
								styles[`files__item_${file.id}`]
							}`}
						/>
					))}
				</div>
			);

		case 'updates':
			return (
				<div className={styles['updates']} aria-hidden='true'>
					<Tool
						image={toolImageToText}
						title={t('benefits_card4_imageTitle')}
						text={t('benefits_card4_imageText')}
						className={styles['updates__tool_prev']}
					/>
					<Tool
						image={toolSpeechToText}
						title={t('benefits_card4_speechTitle')}
						text={t('benefits_card4_speechText')}
						className={styles['updates__tool_main']}
					/>
					<Tool
						image={toolImageToText}
						title={t('benefits_card4_imageTitle')}
						text={t('benefits_card4_imageText')}
						className={styles['updates__tool_next']}
					/>
					<span className={styles['updates__badge']}>
						{t('benefits_card4_badge')}
					</span>
				</div>
			);

		default:
			return null;
	}
};
